import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { ScrollPanel } from 'primereact/scrollpanel';

import { ConfirmDialogService, ToastService, useDataTable, useDialogCrup } from '@iamsoftware/react-hooks';
import { AgreementTemplateService as Service } from './AgreementTemplateService';
import { FormFields } from './FormFields';
import { TemplateDetail } from './TemplateDetail';
import { CommonService } from '../../../service/CommonService';
import { Oauth2GoogleService } from '../../hop-dong/mau-hop-dong/Oauth2GoogleService';
import { UploadExcel } from '../../quan-ly-tai-lieu/UploadExcel';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { AgreementService } from 'src/service/AgreementService';
GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

const statuses = [
  { value: 'N', label: 'Đang hoạt động' },
  { value: 'Y', label: 'Ngừng hoạt động' }
];

// --- EditorDialog component ---
const EditorDialog = ({ display, setDisplay, onSaved }: {
  display: any;
  setDisplay: (v: any) => void;
  onSaved?: (fileId: string, fileName: string) => void;
}) => {
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (display?.fileId) {
      setFileId(display.fileId);
      setFileName(display.fileName || '');
    }
  }, [display]);

  const handleSave = async () => {
    if (!fileId) return;
    if (!fileName.trim()) {
      ToastService.error('Vui lòng nhập tên tài liệu');
      return;
    }
    setSaving(true);
    try {
      const res = await AgreementService.saveFile(fileId, fileName.trim());
      if (res?.success === 'Y' || res?.fileId) {
        ToastService.success('Tài liệu đã được lưu thành công!');
        onSaved?.(fileId, fileName.trim());
        setDisplay(null);
      } else {
        ToastService.error(res?.error || 'Không thể lưu tài liệu');
      }
    } catch {
      ToastService.error('Lỗi khi lưu tài liệu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      header="Soạn thảo tài liệu"
      visible={!!display}
      style={{ width: '98vw' }}
      position="top"
      onHide={() => setDisplay(null)}
      footer={
        <div className="flex justify-content-end gap-2">
          <span className="p-inputgroup" style={{ maxWidth: 360 }}>
            <span className="p-inputgroup-addon"><i className="pi pi-file" /></span>
            <InputText value={fileName} onChange={e => setFileName(e.target.value)} placeholder="Nhập tên tài liệu" />
          </span>
          <Button label="Lưu tài liệu" icon="pi pi-save" type="button" severity="success" size="small"
            loading={saving} onClick={handleSave} />
          <Button label="Đóng" icon="pi pi-times" type="button" outlined severity="secondary" size="small"
            onClick={() => setDisplay(null)} />
        </div>
      }
    >
      {fileId && (
        <iframe
          src={`https://docs.google.com/document/d/${fileId}/edit`}
          width="100%"
          height="800"
          style={{ border: 'none' }}
          referrerPolicy="no-referrer"
        />
      )}
    </Dialog>
  );
};

// --- PDF Preview Dialog using canvas (pdfjs-dist) ---
const PdfPreviewDialog = ({ visible, onHide, fileId, title }: {
  visible: boolean;
  onHide: () => void;
  fileId: string | null;
  title?: string;
}) => {
  const [canvases, setCanvases] = useState<HTMLCanvasElement[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!visible || !fileId) {
      setCanvases([]);
      setCurrentPage(1);
      return;
    }
    loadPdf(fileId);
  }, [visible, fileId]);

  const renderPage = async (pdf: any, pageNumber: number) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    await page.render({ canvasContext: context, viewport }).promise;
    return canvas;
  };

  const loadPdf = async (fId: string) => {
    setLoading(true);
    try {
      const blob = await Oauth2GoogleService.getPdf(fId);
      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const promises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        promises.push(renderPage(pdf, i));
      }
      const result = await Promise.all(promises);
      setCanvases(result);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error rendering PDF:', err);
      ToastService.error('Lỗi khi hiển thị file PDF');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = canvases.length;

  return (
    <Dialog
      header={title || 'Xem tài liệu mẫu'}
      visible={visible}
      position="top"
      draggable={false}
      resizable={false}
      modal
      style={{ width: '70vw', maxHeight: '95vh' }}
      onHide={onHide}
      footer={
        <div className="flex justify-content-between align-items-center">
          <div className="flex align-items-center gap-2">
            {totalPages > 0 && (
              <>
                <Button icon="pi pi-chevron-left" text size="small"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} />
                <span className="text-sm">Trang {currentPage} / {totalPages}</span>
                <Button icon="pi pi-chevron-right" text size="small"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} />
              </>
            )}
          </div>
          <Button label="Đóng" icon="pi pi-times" outlined severity="secondary" size="small"
            onClick={onHide} />
        </div>
      }
    >
      <div className="flex gap-2" style={{ height: 'calc(85vh - 120px)' }}>
        {/* Main content */}
        <div className="flex-1" style={{ backgroundColor: 'var(--surface-ground)', borderRadius: '8px', overflow: 'hidden' }}>
          {loading && (
            <div className="flex align-items-center justify-content-center h-full">
              <div className="text-center">
                <i className="pi pi-spin pi-spinner text-4xl mb-3" style={{ color: 'var(--primary-color)' }} />
                <p>Đang tải PDF...</p>
              </div>
            </div>
          )}
          {!loading && canvases.length > 0 && (
            <ScrollPanel style={{ width: '100%', height: '100%' }}>
              <div className="flex flex-column align-items-center p-3 gap-3">
                {canvases[currentPage - 1] && (
                  <div className="shadow-3" style={{ borderRadius: '4px', overflow: 'hidden', backgroundColor: '#fff' }}>
                    <img
                      src={canvases[currentPage - 1].toDataURL()}
                      alt={`Trang ${currentPage}`}
                      style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                )}
              </div>
            </ScrollPanel>
          )}
          {!loading && canvases.length === 0 && (
            <div className="flex align-items-center justify-content-center h-full">
              <div className="text-center">
                <i className="pi pi-file-pdf text-6xl mb-3" style={{ color: 'var(--text-color-secondary)' }} />
                <p style={{ color: 'var(--text-color-secondary)' }}>Không có file để hiển thị</p>
              </div>
            </div>
          )}
        </div>

        {/* Thumbnail sidebar */}
        {canvases.length > 1 && (
          <div style={{ width: '120px', flexShrink: 0 }}>
            <ScrollPanel style={{ width: '100%', height: '100%' }}>
              <div className="flex flex-column gap-2 p-1">
                {canvases.map((canvas, i) => (
                  <div
                    key={i}
                    className="cursor-pointer transition-all"
                    style={{
                      border: currentPage === i + 1 ? '2px solid var(--primary-color)' : '2px solid transparent',
                      borderRadius: '4px',
                      opacity: currentPage === i + 1 ? 1 : 0.7,
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    <img
                      src={canvas.toDataURL()}
                      alt={`Thumbnail ${i + 1}`}
                      style={{ width: '100%', display: 'block' }}
                    />
                    <div style={{
                      position: 'absolute', bottom: 0, right: 0,
                      backgroundColor: 'var(--primary-color)', color: '#fff',
                      padding: '1px 4px', fontSize: '10px', borderTopLeftRadius: '4px'
                    }}>
                      {i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollPanel>
          </div>
        )}
      </div>
    </Dialog>
  );
};

// --- Main component ---
export const TemplateDocumentConfig: React.FC = () => {
  const dataKey = 'agreementId';
  const [statusFilter, setStatusFilter] = useState(statuses[0].value);
  const [keyword, setKeyword] = useState('');
  const [documentTypeOptions, setDocumentTypeOptions] = useState<any[]>([]);
  const [documentTypeMap, setDocumentTypeMap] = useState<Record<string, string>>({});
  const [displayUpload, setDisplayUpload] = useState(false);
  const [displayFileDetail, setDisplayFileDetail] = useState(false);
  const [formFieldsDisplay, setFormFieldsDisplay] = useState<any>(null);
  const [detailDialog, setDetailDialog] = useState<any>(null);
  const [templateData, setTemplateData] = useState<any>(null);
  const [fileName, setFileName] = useState('');
  const [fileId, setFileId] = useState<string | null>(null);
  const [displayUploadExcel, setDisplayUploadExcel] = useState(false);
  const [selectedTemplateForBulk, setSelectedTemplateForBulk] = useState<any>(null);
  const toast = useRef<Toast>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Editor dialog state
  const [editorDisplay, setEditorDisplay] = useState<any>(null);
  const [editorCreating, setEditorCreating] = useState(false);

  // PDF preview dialog state
  const [pdfPreviewVisible, setPdfPreviewVisible] = useState(false);
  const [pdfPreviewFileId, setPdfPreviewFileId] = useState<string | null>(null);

  useEffect(() => {
    CommonService.getAgreementType().then(data => {
      if (data?.listData) {
        const options = data.listData.map((item: any) => ({
          value: item.agreementPartyTypeConfId,
          label: item.agreementConfigureName
        }));
        setDocumentTypeOptions(options);
        const map: Record<string, string> = {};
        data.listData.forEach((item: any) => {
          if (item.agreementConfigureEnumId) {
            map[item.agreementConfigureEnumId] = item.agreementConfigureName;
          }
        });
        setDocumentTypeMap(map);
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    reloadLazyData();
  }, [statusFilter]);

  const handleTabChange = (e: { index: number }) => {
    setActiveTabIndex(e.index);
    setStatusFilter(statuses[e.index].value);
    clearItems();
    // Use timeout to ensure state is updated before reload
    setTimeout(() => {
      reloadLazyData();
    }, 0);
  };
  const { render: renderDataTable, selectedItems, reloadLazyData, clearItems } = useDataTable({
    tableHeader: (
      <div className="flex align-items-center justify-content-between flex-wrap gap-2">
        <Button
          className="ml-auto"
          label="Tạo tài liệu mẫu"
          icon="pi pi-plus"
          size="small"
          severity="success"
          onClick={handleCreate}
        />
      </div>
    ),
    dataKey,
    columns: [
      {
        field: 'agreementPseudoId',
        header: 'Mã tài liệu mẫu',
        width: 130,
        matchMode: 'contains',
        dataType: 'custom',
        customCell(rowData: any) {
          return (
            <span className="underline text-primary cursor-pointer"
              onDoubleClick={() => handleEdit(rowData, true)}
              title="Double click để xem chi tiết">
              {rowData.agreementPseudoId}
            </span>
          );
        }
      },
      { field: 'agreementName', header: 'Tên tài liệu mẫu', width: 220, matchMode: 'contains' },
      { field: 'fileName', header: 'Tên file mẫu', width: 200, matchMode: 'contains' },
      {
        field: 'agreementConfigureEnumId',
        header: 'Loại tài liệu',
        width: 220,
        dataType: 'custom',
        customCell(rowData: any) {
          const code = rowData.agreementConfigureEnumId;
          return documentTypeMap[code] || code || '';
        }
      },
      { field: 'agreementDate', header: 'Ngày tạo', width: 130, dataType: 'date' },
      { field: 'description', header: 'Mô tả', width: 220, matchMode: 'contains' },
      { field: 'status', header: 'Trạng thái', width: 140 }
    ],
    indexColumnWidth: 45,
    actionColumnWidth: 120,
    getList: lazyLoadEvent => {
      const params = keyword ? { textSearch: keyword.trim() } : undefined;
      return Service.getAgreementList(statusFilter, lazyLoadEvent, params);
    },
    initActions(items: any[]) {
      items?.forEach(item => {
        const actions: any[] = [
          { icon: 'pi pi-pencil', title: 'Chỉnh sửa', className: 'p-button-warning', command: () => handleEdit(item) },
          { icon: 'pi pi-eye', title: 'Xem chi tiết', className: 'p-button-info', command: () => handleEdit(item, true) },
          { icon: 'pi pi-trash', title: 'Xóa', className: 'p-button-danger', command: () => handleDelete(item) }
        ];
        if (item.statusId === 'AGT_ACTIVE') {
          actions.push({
            icon: 'pi pi-lock', title: 'Khóa', className: 'p-button-danger',
            command: () => handleLock(item)
          });
          actions.push({
            icon: 'pi pi-file-excel', title: 'Tạo tài liệu hàng loạt', className: 'p-button-info',
            command: () => handleCreateBulk(item)
          });
        } else if (item.statusId === 'AGT_INACTIVE') {
          actions.push({
            icon: 'pi pi-unlock', title: 'Mở khóa', className: 'p-button-success',
            command: () => handleUnlock(item)
          });
        }
        item.actions = actions;
      });
    },
    originalProps: { scrollHeight: 'calc(100vh - 300px)' }
  });

  const selectedRow = selectedItems?.[dataKey] ? selectedItems : null;

  const { render: renderDialogCrup, create, update } = useDialogCrup({
    header: 'Tài liệu mẫu',
    dataKey,
    width: '42rem',
    fields: [
      { field: 'agreementPseudoId', header: 'Mã tài liệu mẫu', required: true, className: 'md:col-12' },
      { field: 'agreementName', header: 'Tên tài liệu mẫu', required: true, className: 'md:col-12' },
      {
        field: 'agreementPartyTypeConfId',
        header: 'Loại tài liệu',
        required: true,
        className: 'md:col-12',
        type: 'Dropdown',
        DropdownProps: {
          options: documentTypeOptions,
          optionLabel: 'label',
          optionValue: 'value',
          placeholder: 'Chọn loại tài liệu'
        }
      },
      {
        field: 'contentFile',
        header: 'File mẫu (.docx)',
        required: true,
        displayDependency: () => displayUpload,
        type: 'file',
        InputFileProps: { accept: '.docx, .doc' },
        className: 'md:col-12'
      },
      {
        field: 'fileName',
        header: 'File mẫu (.docx)',
        display: displayFileDetail,
        type: 'custom',
        className: 'md:col-12',
        body: (
          <Fragment>
            <label>File mẫu (.docx)</label>
            <div className="p-inputgroup flex-1">
              <InputText value={fileName} disabled />
              <span className="p-inputgroup-addon"
                style={{ background: '#22C55E', color: '#fff', cursor: 'pointer' }}
                onClick={() => {
                  const currentFileId = fileId || templateData?.fileId;
                  if (currentFileId) {
                    setPdfPreviewFileId(currentFileId);
                    setPdfPreviewVisible(true);
                  } else {
                    ToastService.error('Chưa có file để xem');
                  }
                }}>
                <i className="pi pi-eye" />
              </span>
              <span className="p-inputgroup-addon"
                style={{ background: '#3B82F6', color: '#fff', cursor: 'pointer' }}
                title="Chỉnh sửa trên Google Docs"
                onClick={() => {
                  const currentFileId = fileId || templateData?.fileId;
                  if (currentFileId) {
                    setEditorDisplay({ fileId: currentFileId, fileName: fileName || templateData?.fileName || '' });
                  } else {
                    ToastService.error('Chưa có file để chỉnh sửa');
                  }
                }}>
                <i className="pi pi-file-edit" />
              </span>
              <span className="p-inputgroup-addon"
                style={{ background: '#F59E0B', color: '#fff', cursor: 'pointer' }}
                title="Tải lên file mới"
                onClick={() => switchUploadMode()}>
                <i className="pi pi-upload" />
              </span>
            </div>
          </Fragment>
        )
      },
      {
        field: '_editorButton',
        header: '',
        displayDependency: () => displayUpload,
        type: 'custom',
        className: 'md:col-12',
        //body: (
          // <div className="flex align-items-center gap-2">
          //   <span className="text-sm" style={{ color: 'var(--text-color-secondary)' }}>hoặc</span>
          //   <Button
          //     label="Soạn thảo mới"
          //     icon="pi pi-file-edit"
          //     type="button"
          //     size="small"
          //     severity="info"
          //     outlined
          //     loading={editorCreating}
          //     onClick={handleCreateNewDoc}
          //   />
          // </div>
        //)
      },
      { field: 'description', header: 'Mô tả', type: 'InputTextarea', className: 'md:col-12' }
    ],
    createItem: async item => {
      const payload = await normalizePayload(item);
      return Service.create(payload).then(data => {
        if (data.success === 'N' || data.error) {
          ToastService.error(data.error || 'Thêm mới tài liệu mẫu thất bại');
          return Promise.reject(data);
        }
        ToastService.success('Thêm mới tài liệu mẫu thành công');
        if (displayUpload) {
          setFormFieldsDisplay(data);
          setFileId(data.fileId || null);
        }
      }).catch(err => {
        if (err.success === 'N') return;
        console.error('Create error:', err);
        const errorMsg = err.response?.data?.errors || 'Thêm mới tài liệu mẫu thất bại';
        ToastService.error(errorMsg);
        throw err;
      });
    },
    updateItem: async (id, item) => {
      const payload = await normalizePayload(item);
      return Service.update(id, payload).then(data => {
        if (data.success === 'N' || data.error) {
          ToastService.error(data.error || 'Cập nhật tài liệu mẫu thất bại');
          return Promise.reject(data);
        }
        ToastService.success('Cập nhật tài liệu mẫu thành công');
        if (displayUpload) {
          setFormFieldsDisplay(data);
          setFileId(data.fileId || null);
        }
      }).catch(err => {
        if (err.success === 'N') return;
        console.error('Update error:', err);
        const errorMsg = err.response?.data?.errors || 'Cập nhật tài liệu mẫu thất bại';
        ToastService.error(errorMsg);
        throw err;
      });
    },
    reloadLazyData,
    useSaveSplitButton: false
  });

  const normalizePayload = async (item: any) => {
    if (item.contentFile?.length) {
      item.contentFile = item.contentFile[0];
    }
    delete item.categories;
    delete item._editorButton;
    return Service.toFormData(item);
  };

  function resolveDataKey(rowData?: any) {
    if (rowData && rowData[dataKey]) return rowData[dataKey];
    if (selectedRow && selectedRow[dataKey]) return selectedRow[dataKey];
    return null;
  }

  function handleCreate() {
    setDisplayUpload(true);
    setDisplayFileDetail(false);
    setTemplateData(null);
    setFileName('');
    setFileId(null);
    create();
  }

  async function handleCreateNewDoc() {
    setEditorCreating(true);
    try {
      // Backend requires a fileName
      const defaultName = fileName || 'Tai_lieu_mau_moi';
      const res = await Oauth2GoogleService.create({ fileName: defaultName });
      if (res?.fileId || res?.id) {
        const newFileId = res.fileId || res.id;
        setEditorDisplay({ fileId: newFileId, fileName: defaultName });
      } else {
        ToastService.error(res?.error || 'Không thể tạo tài liệu mới');
      }
    } catch (err) {
      console.error('Create new doc error:', err);
      ToastService.error('Lỗi khi tạo tài liệu mới');
    } finally {
      setEditorCreating(false);
    }
  }

  function handleEditorSaved(savedFileId: string, savedFileName: string) {
    setFileId(savedFileId);
    setFileName(savedFileName);
    setDisplayUpload(false);
    setDisplayFileDetail(true);
    reloadLazyData();
  }

  function handleEdit(rowData?: any, viewOnly?: boolean) {
    setDisplayUpload(false);
    setDisplayFileDetail(true);
    const id = resolveDataKey(rowData);
    if (!id) return;

    if (viewOnly) {
      setDetailDialog({ agreementId: id });
      return;
    }

    Service.get(id).then(data => {
      if (data.productCategoryId) {
        data.categories = { value: data.productCategoryId, label: `[${data.categoryPseudoId}] - ${data.categoryName}` };
      }
      setTemplateData(data);
      setFileName(data.fileName);
      setFileId(data.fileId || null);
      update(data);
    });
  }

  function handleLock(item: any) {
    ConfirmDialogService.confirm(
      'Khóa tài liệu mẫu',
      'Bạn xác nhận khóa tài liệu mẫu này?',
      () => {
        Service.disable(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      }
    );
  }

  function handleCreateBulk(item: any) {
    setSelectedTemplateForBulk(item);
    setDisplayUploadExcel(true);
  }

  function handleUnlock(item: any) {
    ConfirmDialogService.confirm(
      'Mở khóa tài liệu mẫu',
      'Bạn xác nhận mở khóa tài liệu mẫu này?',
      () => {
        Service.enable(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      }
    );
  }

  function handleDelete(item: any) {
    ConfirmDialogService.confirm(
      'Xóa tài liệu mẫu',
      'Bạn xác nhận xóa tài liệu mẫu này? Hành động này không thể hoàn tác.',
      () => {
        Service.delete(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        }).catch(() => {
          ToastService.error("Xóa thất bại");
        });
      }
    );
  }

  function switchUploadMode() {
    setDisplayUpload(true);
    setDisplayFileDetail(false);
  }

  return (
    <div className="card border-none shadow-1 border-round-xl" style={{ backgroundColor: 'var(--surface-ground)' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .tai-lieu-mau-table .p-datatable-thead > tr:first-child > th {
          position: sticky !important; top: 0 !important; z-index: 10 !important;
          background: var(--surface-card) !important;
        }
        .tai-lieu-mau-table .p-datatable-thead > tr:nth-child(2) > th {
          position: sticky !important; top: 38px !important; z-index: 9 !important;
          background: var(--surface-card) !important;
        }
      `}} />
      <div className="col-12">
        <h3 className="m-0 mb-2">Quản lý tài liệu mẫu</h3>
      </div>
      <div className="col-12 pb-0">
        <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
          <TabPanel header="Đang hoạt động">
            <div className="mt-2" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
              {renderDataTable()}
            </div>
          </TabPanel>
          <TabPanel header="Ngừng hoạt động">
            <div className="mt-2" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
              {renderDataTable()}
            </div>
          </TabPanel>
        </TabView>
      </div>
      {renderDialogCrup()}
      <FormFields display={formFieldsDisplay} setDisplay={setFormFieldsDisplay} />
      <TemplateDetail display={detailDialog} setDisplay={setDetailDialog} />
      <UploadExcel
        display={displayUploadExcel}
        setDisplay={(value: any) => {
          setDisplayUploadExcel(value);
          if (!value) setSelectedTemplateForBulk(null);
        }}
        reloadLazyData={reloadLazyData}
        agreementId={selectedTemplateForBulk?.agreementId}
      />

      {/* Editor Dialog for Google Docs */}
      <EditorDialog
        display={editorDisplay}
        setDisplay={setEditorDisplay}
        onSaved={handleEditorSaved}
      />

      {/* Canvas-based PDF Preview */}
      <PdfPreviewDialog
        visible={pdfPreviewVisible}
        onHide={() => setPdfPreviewVisible(false)}
        fileId={pdfPreviewFileId}
        title={`Xem tài liệu - ${fileName || 'Tài liệu mẫu'}`}
      />
    </div>
  );
};
